import styled, { css, keyframes } from "styled-components";

const skeletonPulse = keyframes`
  0%, 100% {
    opacity: 0.45;
  }

  50% {
    opacity: 0.9;
  }
`;

export const ModalContainer = styled.div`
  width: min(371px, calc(100vw - 32px));
  border-radius: 24px;
  border: 1px solid #142743;
  background: #0b1727;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  overflow: hidden;
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const Title = styled.h2`
  margin: 0;
  color: #fff;
  font-family: Figtree;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.08px;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }

  &:hover:not(:disabled) {
    opacity: 0.75;
  }

  ${({ $danger }) =>
    $danger &&
    css`
      svg path {
        stroke: #ff3b30;
      }
    `}
`;

export const GridViewport = styled.div`
  max-height: 341px;
  overflow-y: auto;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.14);
    border-radius: 999px;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2px;
`;

const tileBaseStyles = css`
  position: relative;
  width: 100%;
  aspect-ratio: 111.667 / 169;
  border-radius: 2px;
  overflow: hidden;
`;

export const UploadTile = styled.button`
  ${tileBaseStyles}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 16px 12px;
  border: none;
  background: #142743;
  color: #fff;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
    transform: none;
  }

  svg {
    path {
      stroke: white;
    }
  }
`;

export const UploadLabel = styled.span`
  color: #fff;
  font-family: Figtree;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.41px;
  text-align: center;
`;

export const ImageTile = styled.button`
  ${tileBaseStyles}
  padding: 0;
  border: none;
  background: #142743;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
    opacity: 0.72;
    transform: none;
  }
`;

export const TileImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  pointer-events: none;
`;

export const TileVideo = styled.video`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  pointer-events: none;
`;

export const UploadingTile = styled.div`
  ${tileBaseStyles}
  background: #142743;
`;

export const UploadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(12, 12, 14, 0.56);

  l-line-spinner {
    --spinner-color: #fff;
  }
`;

export const MediaIconWrapper = styled.div`
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 2;
  color: #ffffff60;
  pointer-events: none;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const SelectionIndicator = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #fff;
  background: #6eadff;
  color: #fff;
  font-family: Figtree;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.06px;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  pointer-events: none;
`;

export const EmptySelectionIndicator = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #fff;
  background: rgba(12, 12, 14, 0.2);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  pointer-events: none;
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const FooterIconButton = styled(IconButton)`
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const GenerateButton = styled.button`
  flex: 1;
  min-width: 0;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 24px;
  border: none;
  border-radius: 999px;
  background: #fff;
  color: #0f2c32;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 30px rgba(255, 255, 255, 0.16);
  }
`;

export const GenerateButtonText = styled.span`
  font-family: Figtree;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.08px;
  white-space: nowrap;
`;

export const Cost = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: Figtree;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.08px;
  white-space: nowrap;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px 0 12px;
`;

export const EmptyStateIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export const EmptyStateTitle = styled.h3`
  margin: 0;
  color: #fff;
  font-family: Figtree;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.08px;
  line-height: 1.5;
`;

export const EmptyStateDescription = styled.p`
  margin: 0;
  width: 265px;
  color: rgba(255, 255, 255, 0.5);
  font-family: Figtree;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.07px;
  text-align: center;
`;

export const SkeletonTile = styled.div`
  ${tileBaseStyles}
  background: linear-gradient(90deg, #12253e 0%, #203c62 50%, #12253e 100%);
  animation: ${skeletonPulse} 1.2s ease-in-out infinite;
`;
